import React from 'react';
import { graphql, useFragment } from 'react-relay';
import type { goals_nativeIssueTable_GoalsCell$key } from '@atlassian/jira-relay/src/__generated__/goals_nativeIssueTable_GoalsCell.graphql';
import { fg } from '@atlassian/jira-feature-gating';
import { GoalsInlineEditViewWithIsEditable } from '@atlassian/jira-issue-field-goals-inline-edit-full/src/ui/goals/index.tsx';
import {
	useIsSingleLineRowHeightEnabled,
	useIsInlineEditingExtendedFieldSupportEnabled,
} from '../../../../controllers/features/selectors.tsx';

import { useLazyLoadEditability } from '../../../../services/lazy-editability-loader/index.tsx';

import { INLINE_EDIT_ANALYTICS_ATTRIBUTES } from '../common/inline-edit-analytics-attributes.tsx';

export type Props = {
	fieldRef: goals_nativeIssueTable_GoalsCell$key;
};

export const GoalsCell = ({ fieldRef }: Props) => {
	const data = useFragment<goals_nativeIssueTable_GoalsCell$key>(
		graphql`
			fragment goals_nativeIssueTable_GoalsCell on JiraGoalsField
			@argumentDefinitions(isInlineEditingEnabled: { type: "Boolean!" }) {
				id
				lazyIsEditableInIssueView @include(if: $isInlineEditingEnabled)
				...goals_issueFieldGoalsInlineEditFull_GoalsInlineEditViewWithIsEditable_fragmentRef
			}
		`,
		fieldRef,
	);
	const isInlineEditingExtendedFieldSupportEnabled =
		useIsInlineEditingExtendedFieldSupportEnabled();

	// If the property does not exist in fragment data then we know it has been conditionally excluded because inline
	// editing is disabled. In this case we explicitly set isEditable to false to bypass the lazy editability query.
	const isEditable =
		'lazyIsEditableInIssueView' in data &&
		isInlineEditingExtendedFieldSupportEnabled &&
		fg('jsc_goals_inline_edit')
			? data.lazyIsEditableInIssueView
			: false;

	useLazyLoadEditability(data.id, isEditable);
	const shouldTruncate = useIsSingleLineRowHeightEnabled();

	return (
		<GoalsInlineEditViewWithIsEditable
			attributes={INLINE_EDIT_ANALYTICS_ATTRIBUTES}
			spacing="default"
			editViewPopup
			editViewPopupAlignBlock="center"
			fragmentRef={data}
			isEditable={isEditable ?? false}
			menuPosition="fixed"
			readViewFitContainerHeight
			isTruncated={shouldTruncate}
		/>
	);
};
